<template>
  <div class="tracking-page">
    <tracking-page-header
      title="People booking right now"
      subtitle="People actively filling the booking form (activity in the last few minutes). Click a row for their timeline."
      :show-dates="false"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @refresh="loadSessions"
    >
      <template #actions>
        <span class="trk-pill info"><span class="trk-live-dot"></span> Auto-refresh every 30s</span>
      </template>
    </tracking-page-header>

    <div class="trk-insight warn mb-3" v-if="stuckLine">
      <div class="trk-insight-badge">!</div>
      <div>
        <h3>Needs attention</h3>
        <p class="mb-0">{{ stuckLine }}</p>
      </div>
    </div>

    <div class="trk-toolbar-filters mb-3">
      <div class="trk-toolbar-search">
        <i class="fa fa-search trk-toolbar-search-icon"></i>
        <input
          class="form-control"
          v-model="filters.q"
          placeholder="Search name, email, phone"
          @keyup.enter="page = 1; syncAndLoad()"
        />
      </div>
      <select class="form-control trk-toolbar-select" v-model="filters.source" @change="page = 1; syncAndLoad()">
        <option value="">All sources</option>
        <option v-for="opt in sourceOptions" :key="opt.value" :value="opt.value" :title="opt.hint">{{ opt.label }}</option>
      </select>
      <select class="form-control trk-toolbar-select" v-model="filters.booked" @change="page = 1; syncAndLoad()">
        <option value="">Booked: all</option>
        <option value="yes">Booked</option>
        <option value="no">Not booked</option>
      </select>
      <select class="form-control trk-toolbar-select" v-model="filters.deviceType" @change="page = 1; syncAndLoad()">
        <option value="">Any device</option>
        <option value="mobile">Mobile</option>
        <option value="desktop">Desktop</option>
        <option value="tablet">Tablet</option>
      </select>
      <select class="form-control trk-toolbar-select" v-model="filters.step" @change="page = 1; syncAndLoad()">
        <option value="">Any step</option>
        <option v-for="opt in stepOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
      </select>
      <select class="form-control trk-toolbar-select" v-model="filters.status" @change="page = 1; syncAndLoad()">
        <option value="">Live / recent</option>
        <option value="ACTIVE">Browsing now</option>
        <option value="IDLE">Paused</option>
        <option value="ABANDONED">Left without booking</option>
        <option value="COMPLETED">Finished</option>
      </select>
    </div>

    <div class="trk-panel">
      <div class="trk-panel-head">
        <div>
          <h2>Live journeys</h2>
          <p>{{ formatNumber(totalElements) }} journeys match your filters</p>
        </div>
        <span class="trk-pill warn" v-if="stuckCount">{{ stuckCount }} stuck</span>
      </div>
      <div class="trk-panel-body p-0">
        <div class="trk-table-scroll">
        <table class="table trk-table trk-table-clickable mb-0" v-trk-resizable="'live-journeys'">
          <thead>
            <tr>
              <th>Status</th>
              <th>First name</th>
              <th>Last name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Current step</th>
              <th>Device</th>
              <th>Source</th>
              <th>Booked</th>
              <th>Last activity</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in sessions"
              :key="row.sessionId"
              :class="{ 'trk-row-stuck': isStuck(row) }"
              @click="openJourney(row.sessionId)"
            >
              <td>
                <span class="trk-pill" :class="statusClass(row.status)" :title="journeyStatusTitle(row)">
                  <span class="trk-live-dot" v-if="row.status === 'ACTIVE'"></span>
                  {{ prettyJourneyStatus(row) }}
                </span>
                <div v-if="isStuck(row)" class="trk-stuck-hint">{{ stuckHint(row) }}</div>
              </td>
              <td>{{ row.leadFirstName || '-' }}</td>
              <td>{{ row.leadLastName || '-' }}</td>
              <td>{{ row.leadEmail || '-' }}</td>
              <td>{{ row.leadPhone || '-' }}</td>
              <td>{{ prettyStep(row.currentStep) }}</td>
              <td class="text-capitalize">{{ row.deviceType || '-' }}</td>
              <td :title="sourceHint(row.sourceKey, row)">{{ formatSource(row) }}</td>
              <td>
                <span class="trk-pill ok" v-if="row.orderId || row.appointmentId">Booked</span>
                <span class="trk-pill neutral" v-else>Not booked</span>
              </td>
              <td>
                <div>{{ relativeWhen(row.lastActivityAt) }}</div>
                <div class="trk-muted-small">{{ formatWhen(row.lastActivityAt) }}</div>
              </td>
              <td class="text-right" @click.stop>
                <router-link
                  class="btn trk-btn-ghost btn-sm"
                  :to="journeyLink(row.sessionId)"
                >Open</router-link>
              </td>
            </tr>
            <tr v-if="!sessions.length">
              <td colspan="11">
                <div class="trk-empty">
                  <strong>No live journeys right now</strong>
                  Try clearing filters, or open the patient booking page to generate activity.
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
      <div class="trk-panel-body" v-if="totalElements > size">
        <b-pagination
          v-model="page"
          :total-rows="totalElements"
          :per-page="size"
          @change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import { formatNumber, prettyStatus, prettyJourneyStatus, journeyStatusTitle, prettyStep, STEP_FILTER_OPTIONS, formatUkDateTime, formatReferrer, sourceHint, SOURCE_FILTER_OPTIONS, applyJourneyQuery, journeyQuery } from './trackingShared';
import { isJourneyStuck, stuckLabel, stuckSummaryLine } from './trackingConfig';

export default {
  name: 'LiveJourneys',
  components: { TrackingPageHeader },
  data() {
    return {
      sessions: [],
      totalElements: 0,
      page: 1,
      size: 20,
      loading: false,
      refreshTimer: null,
      stepOptions: STEP_FILTER_OPTIONS,
      sourceOptions: SOURCE_FILTER_OPTIONS,
      filters: { status: '', step: '', deviceType: '', source: '', booked: '', q: '' }
    };
  },
  created() {
    applyJourneyQuery((this.$route && this.$route.query) || {}, this);
    this.loadSessions();
    this.refreshTimer = setInterval(this.loadSessions, 30000);
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  },
  computed: {
    stuckCount() {
      return (this.sessions || []).filter((s) => this.isStuck(s)).length;
    },
    stuckLine() {
      return stuckSummaryLine(this.sessions || []);
    },
    chatFilters() {
      return {
        status: this.filters.status || undefined,
        step: this.filters.step || undefined,
        deviceType: this.filters.deviceType || undefined,
        source: this.filters.source || undefined,
        booked: this.filters.booked || undefined,
        q: this.filters.q || undefined,
        activeNow: !this.filters.status || this.filters.status === 'ACTIVE'
      };
    },
    pageSummary() {
      const parts = [`${this.formatNumber(this.totalElements)} journeys in the table`];
      if (this.stuckCount) parts.push(`${this.stuckCount} look stuck`);
      if (this.filters.status) parts.push(`status filter: ${this.filters.status}`);
      if (this.filters.step) parts.push(`step filter: ${this.filters.step}`);
      if (this.filters.deviceType) parts.push(`device: ${this.filters.deviceType}`);
      if (this.filters.source) parts.push(`source: ${this.filters.source}`);
      if (this.filters.booked) parts.push(`booked: ${this.filters.booked}`);
      if (this.filters.q) parts.push(`search: ${this.filters.q}`);
      return parts.join('; ');
    }
  },
  methods: {
    formatNumber,
    prettyStatus,
    prettyJourneyStatus,
    journeyStatusTitle,
    prettyStep,
    sourceHint,
    formatSource(row) {
      return formatReferrer(row);
    },
    isStuck(row) {
      return isJourneyStuck(row);
    },
    stuckHint(row) {
      return stuckLabel(row);
    },
    shortId(id) {
      if (!id) return '-';
      return id.length > 14 ? id.slice(0, 12) + '…' : id;
    },
    journeyLink(sessionId) {
      return {
        path: '/tracking/sessions/' + sessionId,
        query: journeyQuery('', '', this.filters, { from: 'live' })
      };
    },
    openJourney(sessionId) {
      this.$router.push(this.journeyLink(sessionId));
    },
    onPageChange(page) {
      this.page = page;
      this.syncAndLoad();
    },
    syncAndLoad() {
      const query = journeyQuery('', '', this.filters);
      this.$router.replace({ query }).catch(() => {});
      this.loadSessions();
    },
    statusClass(status) {
      if (status === 'ABANDONED') return 'bad';
      if (status === 'IDLE') return 'warn';
      if (status === 'COMPLETED') return 'ok';
      return 'info';
    },
    formatWhen(value) {
      return formatUkDateTime(value);
    },
    relativeWhen(value) {
      if (!value) return '-';
      const sec = Math.floor(Math.max(0, Date.now() - new Date(value).getTime()) / 1000);
      if (sec < 60) return sec + 's ago';
      if (sec < 3600) return Math.floor(sec / 60) + 'm ago';
      return Math.floor(sec / 3600) + 'h ago';
    },
    loadSessions() {
      this.loading = true;
      const params = {
        page: this.page - 1,
        size: this.size,
        sort: 'lastActivityAt,desc',
        status: this.filters.status || undefined,
        step: this.filters.step || undefined,
        deviceType: this.filters.deviceType || undefined,
        source: this.filters.source || undefined,
        booked: this.filters.booked || undefined,
        q: this.filters.q || undefined,
        activeNow: !this.filters.status || this.filters.status === 'ACTIVE'
      };
      this.$onboardingTrackingService.getSessions(params).then((res) => {
        const pageData = (res && res.data) || {};
        this.sessions = pageData.content || [];
        this.totalElements = pageData.totalElements || 0;
      }).finally(() => {
        this.loading = false;
      });
    }
  }
};
</script>
